import { Platform } from "react-native"

import { Event, EventName, Parameter } from "@treefort/lib/analytics"

import { logError } from "../../../logging"

type TransformedValue<TValue> = TValue extends boolean ? string : TValue

export function transformValue<TValue>(
  value: TValue,
): TransformedValue<TValue> {
  return (
    typeof value !== "boolean" ? value : value ? "TRUE" : "FALSE"
  ) as TransformedValue<TValue>
}

export function logFirebaseError(message: string) {
  return (cause: unknown) =>
    logError(new Error(`[Firebase] ${message}`, { cause }))
}

export function transformParameters(
  parameters: Record<string, unknown> | undefined,
) {
  return parameters
    ? Object.fromEntries(
        Object.entries(parameters).flatMap(([key, value]) => {
          switch (key) {
            // Include the deprecated tf_subscribed parameter
            case Parameter.SubscriptionPlanId:
              return [
                [Parameter.SubscriptionPlanId, transformValue(value)],
                ["tf_subscribed", transformValue(Boolean(value))],
              ]
            // Ignore these parameters as they're handled out of the box
            case Parameter.Platform:
            case Parameter.ClientId:
            case Parameter.UserId:
            case Parameter.TenantId:
            case Parameter.UserAuthenticated:
              return []
            default:
              return [[key, transformValue(value)]]
          }
        }),
      )
    : {}
}

export function transformEvents(events: Array<Event>) {
  return events.map((event) => {
    switch (event.name) {
      // Log GA's built-in screen_view event for screen views
      case EventName.ScreenView: {
        const parameters = transformParameters(event.parameters)
        return {
          name: "screen_view",
          parameters: {
            ...parameters,
            ...(Platform.OS === "web"
              ? {
                  firebase_screen: parameters[Parameter.ScreenPath],
                  firebase_screen_class: parameters[Parameter.ScreenPath],
                }
              : {
                  screen_name: parameters[Parameter.ScreenPath],
                  screen_class: parameters[Parameter.ScreenPath],
                }),
          },
        }
      }
      default:
        return {
          name: event.name,
          parameters: transformParameters(event.parameters),
        }
    }
  })
}
